/* The Topic inbox: every source of topic ideas in one list, to browse and
   pick from. Read-and-pick only — editing, approving, batch sends and
   authoring stay in the Topic managers below the generator, and a row links
   there when it has somewhere to go.

   Two kinds of source. Briefs and the queue are rows that already exist;
   one call fetches them, filtered server-side against the client's covered
   titles. Vault suggestions, interview mining and the sitemap scan each run
   a model call, so they run only when their chip's button is pressed and
   show "not run" until then. Opening the inbox costs nothing.
*/
import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Badge, Button } from '@/components/ui';
import { Icon } from '@/lib/icons';
import {
  getTopicInbox, suggestBlogTopics, suggestTopicsFromInterviews, suggestTopicsForPage, getBlogSources, getSitemapPages,
  type SuggestedTopic, type InterviewTopicCandidate, type NearMatch, type BlogQueueItem,
} from '@/data/client';

export type InboxSource = 'vault' | 'vet' | 'sitemap' | 'interviews' | 'briefs' | 'queue';
export type ManagerKey = 'queue' | 'briefs' | 'sitemap' | 'covered';

const LABEL: Record<InboxSource, string> = {
  vault: 'Vault suggestions', vet: 'AI to vet', sitemap: 'Sitemap page', interviews: 'From interviews', briefs: 'Topic idea brief', queue: 'Approved queue',
};
const AI: InboxSource[] = ['vault', 'interviews', 'sitemap'];

export interface InboxHandlers {
  /** A brief or AI-drafted brief: fills the topic and runs the vault check. */
  onPick: (title: string) => void;
  /** A queue row: loads its outline, then checks the vault. */
  onUseQueued: (row: BlogQueueItem) => void;
  /** A queue row: loads and writes at once. */
  onGenerateNow: (row: BlogQueueItem) => void;
  /** A vault suggestion: its coverage is already proven, so it loads whole. */
  onUseVault: (alt: SuggestedTopic) => void;
  /** A sitemap-page candidate: loads with the page it supports. */
  onUsePage: (cand: InterviewTopicCandidate, page: { url: string; anchor: string; keywords: string }) => void;
  /** An interview-mined candidate: loads with its source interviews. */
  onUseMined: (cand: InterviewTopicCandidate, sourceIds: string[]) => void;
  onOpenManager: (key: ManagerKey) => void;
}

interface Row {
  key: string;
  title: string;
  meta: string;
  source: InboxSource;
  near?: NearMatch[];
  manager?: { key: ManagerKey; label: string };
  gen?: boolean;
  use: () => void;
}

/* A row that is close to a covered topic or an article is not shown — that
   is the filter the handoff asked for. Close to a queue row or a brief is
   information, not a reason to hide: it may be the same idea mid-flight. */
const coveredBy = (near?: NearMatch[]) => near?.find((n) => n.where === 'covered' || n.where === 'article');

export function TopicInbox({ h }: { h: InboxHandlers }) {
  const [filter, setFilter] = useState<'all' | InboxSource>('all');
  const { data: inbox, isLoading } = useQuery({ queryKey: ['topicInbox'], queryFn: getTopicInbox, staleTime: 30_000 });

  const [vault, setVault] = useState<SuggestedTopic[] | null>(null);
  const [mined, setMined] = useState<{ cands: InterviewTopicCandidate[]; sourceIds: string[] } | null>(null);
  const [scanned, setScanned] = useState<{ cands: InterviewTopicCandidate[]; page: { url: string; anchor: string; keywords: string } } | null>(null);
  const [busy, setBusy] = useState<InboxSource | null>(null);
  const [note, setNote] = useState('');

  const run = async (src: InboxSource) => {
    setBusy(src); setNote('');
    try {
      if (src === 'vault') {
        const r = await suggestBlogTopics();
        const rows = [{ topic: r.topic, questions: r.questions, gaps: r.gaps, coverage: r.coverage, nearTo: undefined }, ...r.alternatives].filter((t) => t.topic);
        setVault(rows);
        if (r.note) setNote(r.note);
      } else if (src === 'interviews') {
        const { items } = await getBlogSources();
        const ids = items.slice(0, 8).map((s) => s.id);
        if (!ids.length) { setNote('No interviews with a transcript yet.'); setMined({ cands: [], sourceIds: [] }); return; }
        const r = await suggestTopicsFromInterviews(ids);
        setMined({ cands: r.candidates, sourceIds: ids });
        if (r.note) setNote(r.note);
      } else if (src === 'sitemap') {
        const sm = await getSitemapPages();
        const target = sm.focus[0]?.url || sm.pages[0]?.url;
        if (!target) { setNote('No sitemap pages yet — import the sitemap, or set SEO focus pages on the client profile.'); setScanned({ cands: [], page: { url: '', anchor: '', keywords: '' } }); return; }
        const r = await suggestTopicsForPage(target);
        setScanned({ cands: r.candidates, page: r.page ?? { url: target, anchor: '', keywords: '' } });
        if (r.note) setNote(r.note);
      }
    } catch (e) { setNote(e instanceof Error ? e.message : String(e)); }
    finally { setBusy(null); }
  };
  const ran: Record<InboxSource, boolean> = { vault: vault !== null, interviews: mined !== null, sitemap: scanned !== null, vet: true, briefs: true, queue: true };

  const rows: Row[] = [];
  let hiddenAi = 0;
  for (const t of vault ?? []) {
    if (coveredBy(t.nearTo)) { hiddenAi++; continue; }
    rows.push({ key: `v:${t.topic}`, title: t.topic, meta: t.coverage ? `Est. coverage ${t.coverage.score}/100` : 'Not scored', source: 'vault', near: t.nearTo, use: () => h.onUseVault(t) });
  }
  for (const c of mined?.cands ?? []) {
    if (coveredBy(c.nearTo)) { hiddenAi++; continue; }
    rows.push({ key: `i:${c.title}`, title: c.title, meta: c.angle, source: 'interviews', near: c.nearTo, use: () => h.onUseMined(c, mined!.sourceIds) });
  }
  for (const c of scanned?.cands ?? []) {
    if (coveredBy(c.nearTo)) { hiddenAi++; continue; }
    rows.push({ key: `s:${c.title}`, title: c.title, meta: `Supports ${scanned!.page.anchor || scanned!.page.url}`, source: 'sitemap', near: c.nearTo, manager: { key: 'sitemap', label: 'Batch send' }, use: () => h.onUsePage(c, scanned!.page) });
  }
  for (const b of inbox?.briefs ?? []) {
    rows.push({
      key: `b:${b.id}`, title: b.title, source: b.source === 'ai' ? 'vet' : 'briefs',
      meta: [b.source === 'ai' && b.status === 'draft' ? 'AI suggestion awaiting review' : b.status === 'suggested' ? 'Sent to the client' : 'Brief', b.city, b.keywords].filter(Boolean).join(' · '),
      near: b.near, manager: { key: 'briefs', label: 'Open brief' }, use: () => h.onPick(b.title),
    });
  }
  for (const q of inbox?.queue ?? []) {
    rows.push({
      key: `q:${q.id}`, title: q.topic, source: 'queue',
      meta: q.status === 'approved'
        ? (q.autoWrite?.status === 'done' ? 'Approved · written, in review' : q.autoWrite && q.autoWrite.status !== 'failed' ? 'Approved · being written automatically' : 'Approved · ready to write')
        : `Pending · waiting on ${q.waitingOn?.name ?? 'the SEO team'}`,
      /* Generate now would write a topic the sweeper is writing, or has. */
      near: q.near, manager: { key: 'queue', label: 'Edit in queue' }, gen: q.status === 'approved' && !(q.autoWrite && q.autoWrite.status !== 'failed'), use: () => h.onUseQueued(q),
    });
  }
  const shown = rows.filter((r) => filter === 'all' || r.source === filter);
  const count = (s: InboxSource) => rows.filter((r) => r.source === s).length;
  const idle = AI.filter((s) => !ran[s]);
  const hidden = (inbox?.hidden ?? 0) + hiddenAi;

  const RunBtn = ({ s }: { s: InboxSource }) => (
    <Button size="sm" variant="outline" disabled={busy !== null} iconLeft={<Icon name="sparkles" size={13} />} onClick={() => void run(s)}>
      {busy === s ? 'Running…' : s === 'sitemap' ? 'Scan the top focus page' : s === 'interviews' ? 'Mine interviews' : 'Suggest from vault'}
    </Button>
  );

  return (
    <div className="inbox">
      <p className="dash-muted" style={{ margin: 0, fontSize: 'var(--text-xs)' }}>
        Browse every topic source in one list and pick one. Editing, approving and batch sends stay in the Topic managers below.
        {inbox ? ` ${inbox.coveredCount} covered topic${inbox.coveredCount === 1 ? '' : 's'} are filtered out${hidden ? ` (${hidden} hidden here)` : ''}.` : ''}
      </p>
      <div className="inbox__chips">
        <button type="button" className={`inbox__chip${filter === 'all' ? ' is-on' : ''}`} onClick={() => setFilter('all')}>All <i>{rows.length}</i></button>
        {(Object.keys(LABEL) as InboxSource[]).map((s) => {
          const notRun = AI.includes(s) && !ran[s];
          return (
            <button key={s} type="button" className={`inbox__chip${filter === s ? ' is-on' : ''}${notRun ? ' inbox__chip--idle' : ''}`} onClick={() => setFilter(s)}>
              {notRun && <Icon name="sparkles" size={11} />}{LABEL[s]} <i>{notRun ? 'not run' : count(s)}</i>
            </button>
          );
        })}
      </div>
      {filter === 'all' && idle.length > 0 && (
        <div className="inbox__run">
          <span className="dash-muted" style={{ fontSize: 'var(--text-xs)' }}>{idle.length} AI source{idle.length === 1 ? '' : 's'} not run yet. Each is one model call:</span>
          {idle.map((s) => <RunBtn key={s} s={s} />)}
        </div>
      )}
      {note && <p className="dash-muted" style={{ margin: 0, color: 'var(--warning)' }}>{note}</p>}
      <div className="inbox__list">
        {filter !== 'all' && AI.includes(filter) && !ran[filter] ? (
          <div className="inbox__empty">
            <p style={{ margin: '0 0 10px' }}>{LABEL[filter]} hasn’t been run this session. It runs one AI call.</p>
            <RunBtn s={filter} />
          </div>
        ) : isLoading && !rows.length ? (
          <div className="inbox__empty">Loading…</div>
        ) : shown.length ? shown.map((r) => (
          <div key={r.key} className="inbox__row">
            <span className="inbox__main">
              <strong>{r.title}</strong>
              <em>{r.meta}{r.near?.length ? <> · <span className="inbox__near">close to “{r.near[0].title}”</span></> : null}</em>
            </span>
            <span className="inbox__src">{LABEL[r.source]}</span>
            <span className="inbox__acts">
              {r.manager && <button type="button" className="bq-add" onClick={() => h.onOpenManager(r.manager!.key)}>{r.manager.label}</button>}
              {r.gen && <Button size="sm" variant="outline" iconLeft={<Icon name="sparkles" size={13} />} onClick={() => h.onGenerateNow(inbox!.queue.find((q) => `q:${q.id}` === r.key)!)}>Generate now</Button>}
              <Button size="sm" variant="primary" onClick={r.use}>Use topic <Icon name="arrow-right" size={12} /></Button>
            </span>
          </div>
        )) : <div className="inbox__empty">Nothing here yet.</div>}
      </div>
      {inbox && inbox.briefs.length === 0 && inbox.queue.length === 0 && (
        <p className="dash-muted" style={{ margin: 0, fontSize: 'var(--text-xs)' }}>
          No briefs or queued topics for this client. <Badge variant="neutral">tip</Badge> the AI sources above propose from the vault.
        </p>
      )}
    </div>
  );
}
